import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useI18n } from './i18n';
import { ROUTES } from './routes';
import Schedule from './Schedule';
import Reachable from './onboarding/Reachable';
import { nextMark } from './onboarding/gate';
import { useFirstRun } from './onboarding/FirstRun';

/**
 * The first run: configuration inside the wait, and then the chat. F-141, steps 2 and 3.
 *
 * Leo: *"nobody is going to wait 15 minutes for something to happen, they'll go to a different
 * tab or a different app, **or we ask them to do things in the meantime**… the onboarding should
 * take what it takes… **when they're done we know they're reachable**."*
 *
 * So this is not a loading screen and it is not a tour of the product. It is the moment the one
 * setting the product cannot work without gets made, placed at the only point in a learner's life
 * when they have nothing else to do and every reason to cooperate. Research is running behind it.
 *
 * **It takes what it takes.** Nothing here is paced against the research and nothing is padded to
 * fill it: a fast learner finishes and waits on the plan screen, a slow one is still going when
 * research ends, and neither is a problem. There is no progress bar, no step counter and no
 * estimate of how long this will take, because all three would be claims about a race nobody is
 * running.
 *
 * **Three steps and no framework.** The design says onboarding-as-a-system — resumable tours, a
 * step model, progress tracking — is explicitly not wanted: *"This is one flow, once, for a learner
 * who has just arrived."* So the step is a single value in `localStorage` and the screen is a
 * switch on it; reloading the page in the middle simply asks them again.
 *
 * **What moves between the steps is a press, and nothing else.** That is the change this screen
 * exists in its current shape for. The step used to be derived from whether a reach window had been
 * stored, which made saving a setting the same act as finishing a step — Leo: *"i clicked save on
 * my configuration for time and number of messages and never got to notifications and it took me to
 * the next screen, that page needs to auto save and we need to more explicitly tell the user to
 * move on."* Both halves of that answer are here: the schedule panel stores by itself and has no
 * button, and each step ends with one control that says what it does.
 *
 * ---
 *
 * **No plan selector and no plan navigation, and it is worth stating rather than leaving to be
 * discovered** (F-151 step 8; restated 2026-09-20 when the tab bar became a selector in the
 * header). A learner here has not finished setting up their first plan, and a control for
 * choosing *between* plans is the app asking a question the flow has not reached.
 *
 * **It used to be structural in a way that no longer holds, and that is the point of this
 * paragraph.** The old bar was imported by exactly three screens and this was not one of them, so
 * nothing here could have drawn it. The selector lives in the shell, above every screen — so the
 * guarantee has to be *made* rather than inherited, and it is made twice: `showsPlanChrome` in
 * `nav.ts` is a positive list of the addresses the chrome belongs above — `SECTIONS`, plus
 * `/new-plan` since F-163 — and `/welcome` is on none of it, and it refuses again whenever
 * `gateIsClosed`. Two rules because the first covers the beat before
 * the facts land (the stage reads `unknown`, which is not "gated") and the second covers a gated
 * learner anywhere else. `nav.test.ts` asserts both.
 *
 * It would also have little to offer if it were here — a gated learner has exactly one plan,
 * because the gate closes in the minutes after the first one is stated. **That is corroboration
 * and not the argument**, and it is weaker than it looks: with the cap at three since 2026-09-19
 * (tier 99, which is every account's default), the selector would not be empty for them, it
 * would offer to add a second plan to somebody who has not finished setting up their first. An
 * earlier draft of this paragraph leaned on a cap of one and was wrong about it. The rules above
 * do not depend on any of it.
 *
 * The screens below (`Schedule`, and `Reachable`'s notification controls) carry no `?o=` for the
 * same reason `sectionHref` refuses to give them one: they act on settings that belong to the
 * learner, not to a plan. That is also the correction the owner's own phrasing needed — he asked
 * for *"how many questions should nabu ask you about this learning objective every day"*, and the
 * daily number is deliberately **global**, which was his earlier decision (*"Messaging settings is
 * global, I define globally, NOT per objective"*) and is what keeps per-plan reminders bounded. The
 * copy keeps his register and drops the objective; `welcome.plan.many` is where the learner is told
 * why one number covers every plan they have.
 */
export default function Welcome() {
  const { t } = useI18n();
  const { stage, facts, note, mark } = useFirstRun();
  const navigate = useNavigate();

  /**
   * Moving on, which is now a decision the learner makes and not a side effect of saving.
   *
   * **This is the other half of what Leo hit**, and the half that is here rather than in
   * `Schedule`: *"i clicked save on my configuration for time and number of messages and never got
   * to notifications and it took me to the next screen."* The step used to be derived from whether
   * a window had been stored, so storing one *was* the navigation — and with the panel storing by
   * itself, the first nudge of a slider would have done it.
   *
   * So the step is derived from the mark, the mark is written here, and the only thing that writes
   * it is a press. `nextMark` holds which mark follows which stage, in `gate.ts`, where a test can
   * see it.
   */
  /**
   * Set when a press could not advance, so that pressing always does something visible.
   *
   * **The button is never disabled and that is deliberate**: a control greyed out for a reason the
   * screen cannot state is worse than one that occasionally cannot act, and the reason here lives
   * in a panel above rather than in this one. But the first version of that argument claimed the
   * learner would "see this screen rather than a dead button with no explanation", and it was
   * wrong — the press wrote a mark, `firstRun`'s first rule answered `configure` regardless
   * because no window had been stored, the screen re-rendered identically, and nothing was said.
   * It was a dead button with no explanation.
   */
  const [blocked, setBlocked] = useState(false);

  const moveOn = () => {
    /*
     * The gate's own rule, asked before the press rather than after it.
     *
     * `firstRun` reads `windowConfigured` before it reads the mark, so writing `reaching` while no
     * window is stored changes nothing a learner can see. It is reachable: `getWindow` failing
     * inside `Schedule` leaves the panel with no draft and autosave never runs, and a refused save
     * leaves the window unstored with the reason on the panel above. Saying so is the whole fix —
     * the panel is already showing what went wrong, and this points at it.
     */
    if (stage === 'configure' && !facts?.windowConfigured) {
      setBlocked(true);
      return;
    }
    setBlocked(false);
    const next = nextMark(stage);
    if (next) mark(next);
  };

  /*
   * The mark is written when the gate is standing in front of somebody, not when they finish.
   *
   * It is what tells the chat, later, that this learner is owed the worked examples — and it has
   * to be set *here* because the only other evidence is the absence of a reach window, which is
   * the very thing the autosave below removes. A learner who already had a window never reaches
   * this effect — `configure` requires an unsaved one, so the gate never opens in front of them
   * to be walked — and so is never marked, which is half of what keeps the samples off their
   * thread. The other half is that the mark is keyed by learner, because this effect cannot know
   * who was using the browser yesterday.
   *
   * **It is also what makes the whole flow unreachable for a configured account**, and that is
   * now load-bearing rather than incidental: after the window is saved, every remaining step is
   * decided by the mark, so the guarantee that a month-old learner sees none of this rests on
   * this effect being the only writer of the first one.
   *
   * **`!facts?.mark` is what makes "the first one" true**, and without it this effect can send
   * somebody backwards. `GET /api/window` is a plain `GetItem` with no `ConsistentRead`, so a
   * re-read can honestly answer `configured: false` moments after a write — and a learner standing
   * on the notifications step when that lands would be moved to `configure` and have their
   * `reaching` mark overwritten with `configuring`, putting them back at step one with no
   * explanation. The `noted` facts in `FirstRunProvider` protect the learner who saved on *this*
   * screen; they are empty for one who arrived with the step already behind them.
   */
  useEffect(() => {
    if (stage === 'configure' && !facts?.mark) mark('configuring');
  }, [stage, facts?.mark, mark]);

  // Not read yet. A redirect rendered against a reading that has not arrived sends somebody to
  // one screen and then moves them, which reads as the app deciding twice.
  if (stage === 'unknown') return <p className="muted">{t('welcome.deciding')}</p>;

  /*
   * Nothing owed: this address answers with a redirect for the rest of the account's life.
   *
   * That covers the returning learner stating a second objective — *"adding a new objective
   * doesn't do onboarding, that happens once"* — and everybody who was already using nabu when
   * this shipped: both have a saved reach window and neither has ever been marked, and `firstRun`
   * answers `open` for a configured account with no mark whatever else is true of it.
   */
  if (stage === 'open') return <Navigate to={ROUTES.plan} replace />;

  /*
   * The notifications step, behind the same gate and with its own screen.
   *
   * Everything it decides is in `onboarding/reach.ts`; what is here is that it is *rendered by this
   * route*, which is what stops `FirstRunGate` from bouncing a learner between two addresses. The
   * guard hands `/welcome` through for every closed stage, so a closing stage this file did not
   * draw would be a blank page rather than a redirect loop — `onboarding-gate.test.ts` asserts the
   * two lists match.
   */
  if (stage === 'reach') return <Reachable onDone={moveOn} />;

  if (stage === 'tour') {
    return (
      <section className="panel">
        <h2>{t('welcome.done.title')}</h2>
        <p className="muted">{t('welcome.done.body')}</p>
        <div className="actions">
          {/*
           * To the chat, where the examples are rendered — rather than showing them here.
           *
           * The design is specific about this: *"Then the chat, pre-filled with worked
           * examples."* A briefing shown on a setup screen teaches the shape and not the place;
           * the point is that the learner recognises the thing when it turns up in the thread
           * they will be living in, so the illustration has to be in that thread.
           */}
          <button
            type="button"
            className="button primary"
            onClick={() => navigate(ROUTES.chat, { replace: true })}
          >
            {t('welcome.done.go')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="panel">
        <h2>{t('welcome.title')}</h2>
        <p className="muted">{t('welcome.lede')}</p>

        {/*
         * Teaching, because this is the only moment it will be read.
         *
         * Leo, walking his own flow: *"We are asking for plan related config when the user has no
         * idea what a plan is"* — and then, on what to do about it: *"keep the gate change the
         * framing but take advantage to educate. Explain what a plan is, that you can have
         * multiple."* The gate stays exactly where it was; what changes is that the screen now
         * earns the thing it is asking for. A learner waiting on a research pass with nothing else
         * to do is the cheapest attention this product will ever have, and the sentence below is
         * the one that makes the control underneath it make sense.
         */}
        <div className="field">
          <strong>{t('welcome.plan.title')}</strong>
          <small>{t('welcome.plan.body')}</small>
          {/*
           * That there can be several, said here and not on the screen where a second one is
           * created — because this is where the learner is being asked for a number that will be
           * shared between them, and "how many questions a day" is a different question once you
           * know it is not per plan. No count is named: the cap comes off the tier on the learner's
           * row and this screen does not read it.
           */}
          <small>{t('welcome.plan.many')}</small>
        </div>

        {/*
         * Why this is mandatory, said out loud, once.
         *
         * A gate whose reason is not given reads as an arbitrary obstacle, and the reason here is
         * genuinely good: this is the one setting without which nothing the product does gets
         * delivered. Saying it is also what stops the next reader softening the gate into a nag,
         * which is the thing it replaced.
         */}
        <p className="muted small">{t('welcome.why')}</p>
      </section>

      {/*
       * The reach window itself — the same component the configuration screen renders.
       *
       * One implementation, deliberately. A "guided" copy of these three controls would be a
       * second place the cap is validated, the spacing sentence is composed and the timezone
       * caveat is explained, and the two would disagree the first time one of them changed. What
       * this screen adds is the framing above and the consequence below; the controls are the
       * controls.
       */}
      {/*
       * **Told, not asked**, the same fix as the create above and for the same failure.
       *
       * `reread` was here, and `GET /api/window` reads with a plain `GetItem` — no `ConsistentRead`
       * (`packages/api/src/window.ts`). A re-read issued milliseconds after the write can
       * legitimately answer `configured: false`, which re-closes the gate under a learner looking
       * at the panel's own success message, with nothing polling to correct it: the only way out
       * was pressing Save again. `Schedule` already knows the truth — it has the stored window in
       * its hands — so the fact is handed over rather than re-derived.
       */}
      <Schedule autosave onSaved={() => note({ windowConfigured: true })} />

      {/*
       * The way on, said as plainly as the owner asked for.
       *
       * *"we need to more explicitly tell the user to move on."* It is a panel of its own rather
       * than a button tucked under the schedule, because the thing it has to be distinguishable
       * from is the panel above it — which no longer has a button at all, and says so.
       *
       * **Always enabled, and never silent.** The panel above stores an unconfigured window shortly
       * after it loads (see `AUTOSAVE_MS`), so by the time anybody has read this far there is
       * normally a window. When there is not — the window could not be read, or the service
       * refused it — the gate would answer `configure` again and the screen would re-render
       * identically, which is a dead button. So the press says why instead, and points at the
       * panel that is already showing the reason. Disabling it would be worse: a control greyed
       * out for a reason this panel cannot state explains nothing at all.
       */}
      <section className="panel">
        <p className="muted small">{t('welcome.next.hint')}</p>
        <div className="actions">
          <button type="button" className="button primary" onClick={moveOn}>
            {t('welcome.next')}
          </button>
        </div>
        {blocked && (
          <p className="problem" role="alert">
            {t('welcome.next.blocked')}
          </p>
        )}
      </section>
    </>
  );
}
